import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Search, Plus, Check, X, Building2, ChevronDown } from "lucide-react";

/**
 * Combobox that lets you search existing clients OR inline-create a new one.
 * Never leaves the page — keystrokes never feel modal.
 */
export default function ClientCombobox({ value, onChange, autoFocus }) {
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [newClient, setNewClient] = useState({ company_name: "", contact_name: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const [highlight, setHighlight] = useState(0);

  const load = async () => {
    const { data } = await api.get("/clients");
    setClients(data);
  };
  useEffect(() => { load(); }, []);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setCreating(false);
      }
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, []);

  const selected = clients.find((c) => c.id === value);

  const filtered = useMemo(() => {
    if (!query) return clients.slice(0, 50);
    const s = query.toLowerCase();
    return clients
      .filter((c) =>
        c.company_name.toLowerCase().includes(s) ||
        (c.contact_name || "").toLowerCase().includes(s) ||
        (c.email || "").toLowerCase().includes(s)
      )
      .slice(0, 50);
  }, [clients, query]);

  const startCreate = () => {
    setCreating(true);
    setNewClient({ company_name: query, contact_name: "", email: "", phone: "" });
  };

  const createAndSelect = async () => {
    if (!newClient.company_name.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/clients", newClient);
      setClients((s) => [data, ...s]);
      onChange(data.id);
      setCreating(false);
      setOpen(false);
      setQuery("");
    } catch (e) {
      alert(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleKey = (e) => {
    if (creating) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, filtered.length)); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
    if (e.key === "Enter") {
      e.preventDefault();
      if (highlight === filtered.length) startCreate();
      else if (filtered[highlight]) { onChange(filtered[highlight].id); setOpen(false); setQuery(""); }
    }
    if (e.key === "Escape") { setOpen(false); inputRef.current?.blur(); }
  };

  return (
    <div className="relative" ref={rootRef} data-testid="client-combobox">
      <div
        onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 0); }}
        className="input flex items-center gap-2 cursor-text"
        style={{ minHeight: 44 }}
      >
        <Building2 className="w-4 h-4 shrink-0" style={{ color: "var(--text-faint)" }} />
        {selected && !open ? (
          <>
            <span className="flex-1 truncate" style={{ color: "var(--text-primary)" }}>
              {selected.company_name}
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); onChange(""); setOpen(true); }}
              className="text-xs"
              style={{ color: "var(--text-faint)" }}
              data-testid="client-clear"
            >
              <X className="w-4 h-4" />
            </button>
          </>
        ) : (
          <input
            ref={inputRef}
            autoFocus={autoFocus}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(0); }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKey}
            placeholder={selected ? selected.company_name : "Type a client name…"}
            className="flex-1 bg-transparent outline-none text-sm"
            style={{ color: "var(--text-primary)" }}
            data-testid="client-combobox-input"
          />
        )}
        <ChevronDown className="w-4 h-4 shrink-0" style={{ color: "var(--text-faint)" }} />
      </div>

      {open && (
        <div
          className="absolute z-30 left-0 right-0 mt-2 surface overflow-hidden shadow-2xl"
          style={{ background: "var(--surface-2)" }}
        >
          {!creating && (
            <>
              <div className="max-h-72 overflow-y-auto py-1">
                {filtered.length === 0 && !query && (
                  <div className="px-4 py-6 text-sm text-center" style={{ color: "var(--text-muted)" }}>
                    No clients yet.
                  </div>
                )}
                {filtered.map((c, i) => (
                  <button
                    key={c.id}
                    onClick={() => { onChange(c.id); setOpen(false); setQuery(""); }}
                    onMouseEnter={() => setHighlight(i)}
                    data-testid={`client-option-${c.id}`}
                    className="w-full text-left px-4 py-2.5 flex items-center justify-between transition-colors"
                    style={{ background: highlight === i ? "var(--surface-3)" : "transparent" }}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate" style={{ color: "var(--text-primary)" }}>
                        {c.company_name}
                      </div>
                      {(c.contact_name || c.email) && (
                        <div className="text-xs truncate" style={{ color: "var(--text-faint)" }}>
                          {c.contact_name}{c.contact_name && c.email ? " · " : ""}{c.email}
                        </div>
                      )}
                    </div>
                    {value === c.id && <Check className="w-4 h-4" style={{ color: "var(--accent)" }} />}
                  </button>
                ))}
              </div>
              <button
                onClick={startCreate}
                onMouseEnter={() => setHighlight(filtered.length)}
                data-testid="client-create-inline-btn"
                className="w-full text-left px-4 py-3 flex items-center gap-2 border-t transition-colors"
                style={{
                  borderColor: "var(--border-subtle)",
                  background: highlight === filtered.length ? "var(--gradient-accent-soft)" : "transparent",
                  color: "var(--accent)",
                }}
              >
                <Plus className="w-4 h-4" />
                <span className="text-sm font-bold tracking-wider uppercase">
                  Create new client{query ? ` "${query}"` : ""}
                </span>
              </button>
            </>
          )}

          {creating && (
            <div className="p-4 space-y-3" data-testid="client-quick-create">
              <div className="eyebrow-accent">Quick Create</div>
              <input
                autoFocus
                value={newClient.company_name}
                onChange={(e) => setNewClient({ ...newClient, company_name: e.target.value })}
                placeholder="Company name *"
                data-testid="quick-client-company"
                className="input"
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  value={newClient.contact_name}
                  onChange={(e) => setNewClient({ ...newClient, contact_name: e.target.value })}
                  placeholder="Contact (optional)"
                  data-testid="quick-client-contact"
                  className="input"
                />
                <input
                  value={newClient.phone}
                  onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                  placeholder="Phone (optional)"
                  data-testid="quick-client-phone"
                  className="input"
                />
              </div>
              <input
                value={newClient.email}
                onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                placeholder="Email (optional)"
                data-testid="quick-client-email"
                className="input"
                onKeyDown={(e) => { if (e.key === "Enter") createAndSelect(); }}
              />
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setCreating(false)} className="btn btn-ghost btn-sm">Cancel</button>
                <button
                  onClick={createAndSelect}
                  disabled={saving || !newClient.company_name.trim()}
                  data-testid="quick-client-save-btn"
                  className="btn btn-primary btn-sm"
                >
                  {saving ? "Creating…" : "Create & Use"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
